import React from 'react';
import axiosInstance from '../../../../api/axiosConfig';
import { FiEye, FiEdit2, FiUserPlus, FiCalendar, FiUser, FiFlag, FiCheckCircle, FiClock, FiTag, FiPaperclip, FiLink, FiList, FiTrash2 } from 'react-icons/fi';
import './ViewTaskModal.css';

const ViewTaskModal = ({
    task,
    subtasks,
    subtasksLoading,
    onClose,
    onEdit,
    onReassign,
    onSubtaskStatusChange,
    onAddSubtask,
    onEditSubtask,
    onDeleteSubtask,
    formatDate,
    formatDateTime
}) => {
    if (!task) return null;

    const getPriorityClass = (priority) => {
        const classes = {
            low: 'low',
            medium: 'medium',
            high: 'high',
            urgent: 'urgent'
        };
        return classes[priority] || 'medium';
    };

    const getStatusClass = (status) => {
        const classes = {
            'todo': 'todo',
            'in-progress': 'in-progress',
            'review': 'review',
            'completed': 'completed'
        };
        return classes[status] || 'todo';
    };

    const getApprovalClass = (approvalStatus) => {
        const classes = {
            'pending': 'pending',
            'approved': 'approved',
            'rejected': 'rejected'
        };
        return classes[approvalStatus] || 'pending';
    };

    const getStatusLabel = (status) => {
        const labels = {
            'todo': 'To Do',
            'in-progress': 'In Progress',
            'review': 'Review',
            'completed': 'Completed'
        };
        return labels[status] || status;
    };

    const getApprovalLabel = (approvalStatus) => {
        const labels = {
            'pending': 'Pending',
            'approved': 'Approved',
            'rejected': 'Rejected'
        };
        return labels[approvalStatus] || approvalStatus;
    };

    return (
        <div className="view-task-modal-overlay" onClick={onClose}>
            <div className="view-task-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="view-task-modal-header">
                    <div className="view-task-modal-title-section">
                        <h2>{task.title}</h2>
                        <button 
                            className="view-task-close-btn" 
                            onClick={onClose}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="view-task-modal-body">
                    {/* Action Buttons */}
                    <div className="view-detail-actions">
                        <button 
                            type="button" 
                            onClick={onReassign} 
                            className="btn-action"
                        >
                            <FiUserPlus size={18} />
                            Reassign
                        </button>
                        <button 
                            type="button" 
                            onClick={onEdit} 
                            className="btn-action"
                        >
                            <FiEdit2 size={18} />
                            Edit Task
                        </button>
                    </div>

                    {/* Basic Information */}
                    <div className="detail-section">
                        <div className="detail-section-title">
                            <FiEye size={20} />
                            Basic Information
                        </div>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <div className="detail-label">Task ID</div>
                                <div className="detail-value">
                                    <span className="enhanced-task-id-badge">
                                        {task.taskId || 'N/A'}
                                    </span>
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Priority</div>
                                <div className="detail-value">
                                    <span className={`enhanced-priority-badge ${getPriorityClass(task.priority)}`}>
                                        {task.priority}
                                    </span>
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Status</div>
                                <div className="detail-value">
                                    <span className={`enhanced-status-badge ${getStatusClass(task.status)}`}>
                                        {getStatusLabel(task.status)}
                                    </span>
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Due Date</div>
                                <div className="detail-value">
                                    <FiCalendar size={16} style={{ marginRight: '6px', color: '#6b7280' }} />
                                    {formatDate(task.dueDate)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="detail-section">
                        <div className="detail-section-title">
                            Description
                        </div>
                        <div className="enhanced-description-box">
                            {task.description || 'No description provided'}
                        </div>
                    </div>

                    {/* Assignment & Approval */}
                    <div className="detail-section">
                        <div className="detail-section-title">
                            <FiUser size={20} />
                            Assignment & Approval
                        </div>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <div className="detail-label">Assigned To</div>
                                <div className="detail-value-multi">
                                    {Array.isArray(task.assignedToName) 
                                        ? task.assignedToName.map((name, idx) => (
                                            <span key={idx} className="user-chip">{name}</span>
                                          ))
                                        : <span className="user-chip">{task.assignedToName}</span>
                                    }
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Assigned By</div>
                                <div className="detail-value">
                                    {task.assignedByName}
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Approval Status</div>
                                <div className="detail-value">
                                    <span className={`enhanced-approval-badge ${getApprovalClass(task.approvalStatus)}`}>
                                        {getApprovalLabel(task.approvalStatus)}
                                    </span>
                                    {task.approvedByName && (
                                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                            by {task.approvedByName}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">Verification</div>
                                <div className="detail-value">
                                    {task.verified ? (
                                        <span className="enhanced-verified-badge">
                                            <FiCheckCircle size={16} />
                                            Verified by {task.verifiedByName}
                                        </span>
                                    ) : (
                                        <span className="enhanced-not-verified-badge">
                                            {task.status === 'completed' ? 'Pending Verification' : 'Not Applicable'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rejection Reason */}
                    {task.rejectionReason && (
                        <div className="detail-section">
                            <div className="detail-section-title" style={{ color: '#dc2626' }}>
                                Rejection Reason
                            </div>
                            <div className="enhanced-description-box" style={{ borderLeftColor: '#dc2626', background: '#fef2f2' }}>
                                {task.rejectionReason}
                            </div>
                        </div>
                    )}

                    {/* Tags */}
                    {task.tags && task.tags.length > 0 && (
                        <div className="detail-section">
                            <div className="detail-section-title">
                                <FiTag size={20} />
                                Tags
                            </div>
                            <div className="enhanced-tags-container">
                                {task.tags.map((tag, idx) => (
                                    <span key={idx} className="enhanced-tag">{tag}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Attachments */}
                    {task.attachments && task.attachments.length > 0 && (
                        <div className="detail-section">
                            <div className="detail-section-title">
                                <FiPaperclip size={20} />
                                Attachments ({task.attachments.length})
                            </div>
                            <div className="enhanced-attachments-list">
                                {task.attachments.map((att, idx) => (
                                    <div
                                        key={idx}
                                        className="enhanced-attachment-item"
                                        onClick={async () => {
                                            try {
                                                const response = await axiosInstance.get(
                                                    `/api/tasks/${task._id}/attachments/${att._id}/download`,
                                                    {
                                                        responseType: 'blob'
                                                    }
                                                );
                                                
                                                const url = window.URL.createObjectURL(response.data);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = att.originalName;
                                                document.body.appendChild(a);
                                                a.click();
                                                window.URL.revokeObjectURL(url);
                                                document.body.removeChild(a);
                                            } catch (err) {
                                                console.error(err);
                                                alert('Failed to download file');
                                            }
                                        }}
                                    >
                                        <span className="enhanced-attachment-icon">📎</span>
                                        <div className="enhanced-attachment-info">
                                            <div className="enhanced-attachment-name">{att.originalName}</div>
                                            <div className="enhanced-attachment-meta">
                                                Uploaded {formatDate(att.uploadedAt)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Linked Tasks */}
                    {task.connections && task.connections.length > 0 && (
                        <div className="detail-section">
                            <div className="detail-section-title">
                                <FiLink size={20} />
                                Linked Tasks ({task.connections.length})
                            </div>
                            <div className="enhanced-linked-tasks">
                                {task.connections.map((conn, idx) => (
                                    <div key={conn._id || idx} className="enhanced-linked-task-item">
                                        <div className="enhanced-linked-task-info">
                                            <div>
                                                <span className="enhanced-linked-task-id">
                                                    {conn.task?.taskId || conn.task?._id || 'N/A'}
                                                </span>
                                                <span className="enhanced-linked-task-type">
                                                    ({conn.type})
                                                </span>
                                            </div>
                                            <div className="enhanced-linked-task-title">
                                                {conn.task?.title || 'Unknown Task'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Reassignment History */}
                    {task.reassignmentHistory && task.reassignmentHistory.length > 0 && (
                        <div className="detail-section">
                            <div className="detail-section-title">
                                <FiClock size={20} />
                                Reassignment History
                            </div>
                            <div className="enhanced-reassignment-list">
                                {task.reassignmentHistory.map((r, idx) => (
                                    <div key={idx} className="enhanced-reassignment-item">
                                        <strong>{r.fromNames?.join(', ') || '—'}</strong> → <strong>{r.toNames?.join(', ') || '—'}</strong>
                                        {r.reassignedByName && <span> by {r.reassignedByName}</span>}
                                        {r.reason && <div style={{ marginTop: '4px', fontStyle: 'italic' }}>{r.reason}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Subtasks Section */}
                    {!task.parentTask && (
                        <div className="detail-section">
                            <div className="detail-section-title">
                                <FiList size={20} />
                                Subtasks ({subtasks?.length || 0})
                                {onAddSubtask && (
                                    <button 
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onAddSubtask();
                                        }}
                                        className="btn-add-subtask-inline"
                                        title="Add Subtask"
                                    >
                                        <FiList size={16} />
                                        Add Subtask
                                    </button>
                                )}
                            </div>
                            {subtasksLoading ? (
                                <div className="subtasks-loading">Loading subtasks...</div>
                            ) : subtasks && subtasks.length > 0 ? (
                                <div className="subtasks-list">
                                    {subtasks.map((subtask) => (
                                        <div key={subtask._id} className="subtask-item">
                                            <div className="subtask-header">
                                                <div className="subtask-title-section">
                                                    <span className="subtask-id">{subtask.taskId}</span>
                                                    <span className="subtask-title">{subtask.title}</span>
                                                </div>
                                                <div className="subtask-status-section">
                                                    <select 
                                                        value={subtask.status} 
                                                        onChange={(e) => onSubtaskStatusChange(subtask._id, e.target.value)}
                                                        className={`subtask-status-select ${getStatusClass(subtask.status)}`}
                                                    >
                                                        <option value="todo">To Do</option>
                                                        <option value="in-progress">In Progress</option>
                                                        <option value="review">Review</option>
                                                        <option value="completed">Completed</option>
                                                    </select>
                                                    {onEditSubtask && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onEditSubtask(subtask);
                                                            }}
                                                            className="subtask-action-btn subtask-edit-btn"
                                                            title="Edit Subtask"
                                                        >
                                                            <FiEdit2 size={14} />
                                                        </button>
                                                    )}
                                                    {onDeleteSubtask && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (window.confirm('Are you sure you want to delete this subtask?')) {
                                                                    onDeleteSubtask(subtask._id);
                                                                }
                                                            }}
                                                            className="subtask-action-btn subtask-delete-btn"
                                                            title="Delete Subtask"
                                                        >
                                                            <FiTrash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="subtask-details">
                                                <div className="subtask-description">
                                                    {subtask.description}
                                                </div>
                                                <div className="subtask-meta">
                                                    <span className={`subtask-priority ${getPriorityClass(subtask.priority)}`}>
                                                        {subtask.priority}
                                                    </span>
                                                    <span className="subtask-assignee">
                                                        {Array.isArray(subtask.assignedToName) 
                                                            ? subtask.assignedToName.join(', ') 
                                                            : subtask.assignedToName}
                                                    </span>
                                                    <span className="subtask-due-date">
                                                        Due: {formatDate(subtask.dueDate)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="no-subtasks">
                                    No subtasks found
                                    {onAddSubtask && (
                                        <button 
                                            type="button"
                                            onClick={onAddSubtask}
                                            className="btn-add-first-subtask"
                                        >
                                            <FiList size={18} />
                                            Create First Subtask
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="view-task-modal-footer">
                    <button type="button" onClick={onClose} className="btn-secondary">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewTaskModal;
